import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getWartsilaEndUserDeclarationConfirmation,
  getWartsilaEndUserDeclaration,
  confirmWartsilaEndUserDeclaration,
} from 'modules/simulatorTrainings/common/api/simulatorTrainingUserDeclarationService'
import { EndUserDeclarationUtils } from 'modules/simulatorTrainings/common/utils'
import {
  SimulatorTrainingEndUserDeclarationState,
  ApiGetWartsilaEndUserDeclarationConfirmationResponse,
  ApiGetWartsilaEndUserDeclarationResponse,
} from './types'

const initialState: SimulatorTrainingEndUserDeclarationState = {
  userDeclarationConfirmationData: {},
  userDeclarationData: {},
  confirmUserDeclarationRequest: {},
  isModalOpen: false,
}

const simulatorTrainingEndUserDeclarationSlice = createSlice({
  name: 'simulatorTrainingEndUserDeclaration',
  initialState,
  reducers: {
    userDeclarationConfirmationLoaded(
      state,
      action: PayloadAction<
        ApiMiddlewarePayload<ApiGetWartsilaEndUserDeclarationConfirmationResponse>
      >
    ) {
      state.userDeclarationConfirmationData = action.payload
    },
    userDeclarationDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiGetWartsilaEndUserDeclarationResponse>>
    ) {
      state.userDeclarationData = action.payload
    },
    confirmUserDeclarationRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.confirmUserDeclarationRequest = action.payload
    },
    modalVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  userDeclarationConfirmationLoaded,
  userDeclarationDetailsLoaded,
  confirmUserDeclarationRequested,
  modalVisibilityChanged,
} = simulatorTrainingEndUserDeclarationSlice.actions
export default simulatorTrainingEndUserDeclarationSlice.reducer

const handleLoadDeclarationConfirmation =
  (
    response: ApiMiddlewarePayload<ApiGetWartsilaEndUserDeclarationConfirmationResponse>
  ): AppThunk =>
  async dispatch => {
    const { error, result } = response

    if (!error && result?.confirmation) {
      const { isConfirmed } = result.confirmation
      if (isConfirmed === false) {
        dispatch(loadEndUserDeclaration() as MiddlewareApiAction)
      } else {
        EndUserDeclarationUtils.markDeclarationViewed()
      }
    }
    dispatch(userDeclarationConfirmationLoaded(response))
  }

export const loadUserDeclarationConfirmation = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      confirmation: () => getWartsilaEndUserDeclarationConfirmation(),
    },
    payloadAction: userDeclarationConfirmationLoaded,
    nextAction: handleLoadDeclarationConfirmation,
    shouldCallApi: () =>
      EndUserDeclarationUtils.getDeclarationState() !== EndUserDeclarationUtils.viewed,
  })
}

const handleLoadDeclarationDetails =
  (response: ApiMiddlewarePayload<ApiGetWartsilaEndUserDeclarationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response

    if (!error && result?.details.endUserDeclarationText) {
      dispatch(modalVisibilityChanged(true))
    }
    dispatch(userDeclarationDetailsLoaded(response))
  }

export const loadEndUserDeclaration = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getWartsilaEndUserDeclaration(),
    },
    payloadAction: userDeclarationDetailsLoaded,
    nextAction: handleLoadDeclarationDetails,
    shouldCallApi: () =>
      EndUserDeclarationUtils.getDeclarationState() !== EndUserDeclarationUtils.viewed,
  })
}

const handleUserDeclarationConfirmation =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    const { error, result } = response
    const ALREADY_CONFIRMED_ERROR_CODE =
      'ConfirmWartsilaEndUserDeclarationDefinitionAlreadyConfirmedResponse'

    if ((!error && result) || error?.code === ALREADY_CONFIRMED_ERROR_CODE) {
      dispatch(modalVisibilityChanged(false))
      EndUserDeclarationUtils.markDeclarationViewed()
    }
    dispatch(confirmUserDeclarationRequested(response))
  }

export const confirmEndUserDeclaration = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => confirmWartsilaEndUserDeclaration(),
    },
    payloadAction: confirmUserDeclarationRequested,
    nextAction: handleUserDeclarationConfirmation,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
